import { Button, Typography } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import { DialogBase } from './DialogBase';

const DEFAULT_AUTO_CONFIRM_MS = 5000;
const TICK_MS = 1000;

interface TimedConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  autoConfirmMs?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog that submits on its own when the countdown runs out.
 */
export const TimedConfirmDialog: React.FC<TimedConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel,
  autoConfirmMs = DEFAULT_AUTO_CONFIRM_MS,
  onConfirm,
  onCancel,
}) => {
  const translate = useTranslate();
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(autoConfirmMs / TICK_MS));
  // Auto-confirm and a click on the button must not both submit.
  const isSettledRef = useRef(false);
  // Held in a ref so a parent re-render cannot restart the countdown.
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  useEffect(() => {
    if (!open) return;

    isSettledRef.current = false;
    setSecondsLeft(Math.ceil(autoConfirmMs / TICK_MS));

    const tick = setInterval(
      () => setSecondsLeft((prev) => Math.max(prev - 1, 0)),
      TICK_MS
    );
    const deadline = setTimeout(() => {
      if (isSettledRef.current) return;
      isSettledRef.current = true;
      onConfirmRef.current();
    }, autoConfirmMs);

    return () => {
      clearInterval(tick);
      clearTimeout(deadline);
    };
  }, [open, autoConfirmMs]);

  const settle = (action: () => void) => {
    if (isSettledRef.current) return;
    isSettledRef.current = true;
    action();
  };

  return (
    <DialogBase
      open={open}
      title={title}
      DialogProps={{ maxWidth: 'xs' }}
      actions={
        <>
          <Button variant="contained" color="primary" onClick={() => settle(onConfirm)}>
            {`${confirmLabel} (${secondsLeft})`}
          </Button>
          <Button variant="outlined" onClick={() => settle(onCancel)}>
            {translate('osfc.buttons.cancel')}
          </Button>
        </>
      }
    >
      <Typography component="div" variant="body1" style={{ textAlign: 'center' }}>
        {message}
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
        {translate('osfc.storage_ui.inventorization.confirm_auto_submit', { seconds: secondsLeft })}
      </Typography>
    </DialogBase>
  );
};
